import { useState, useRef, type ChangeEvent } from 'react';

interface ImageUploadProps {
    onImageSelect: (file: File) => void;
    previewUrl?: string;
    isUploading?: boolean;
    className?: string;
}

export function ImageUpload({
    onImageSelect,
    previewUrl,
    isUploading = false,
    className = '',
}: ImageUploadProps) {
    const [dragOver, setDragOver] = useState(false);
    const [localPreview, setLocalPreview] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFile(file);
        }
    };

    const handleFile = (file: File) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert('Image must be less than 5MB');
            return;
        }

        // Create local preview
        const reader = new FileReader();
        reader.onload = (e) => {
            setLocalPreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);

        onImageSelect(file);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            handleFile(file);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const displayPreview = previewUrl || localPreview;

    return (
        <div className={className}>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
            />

            {displayPreview ? (
                <div className="relative group">
                    <img
                        src={displayPreview}
                        alt="Preview"
                        className="w-full h-48 object-cover rounded-xl border-2 border-gray-200"
                    />
                    {isUploading && (
                        <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center">
                            <div className="text-white font-semibold animate-pulse">Uploading...</div>
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={() => {
                            setLocalPreview(null);
                            if (inputRef.current) inputRef.current.value = '';
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                        ×
                    </button>
                </div>
            ) : (
                <div
                    onClick={() => inputRef.current?.click()}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`
            border-2 border-dashed rounded-xl p-8 text-center cursor-pointer
            transition-all hover:border-primary hover:bg-primary/5
            ${dragOver ? 'border-primary bg-primary/10' : 'border-gray-300'}
          `}
                >
                    <div className="text-4xl mb-2">📷</div>
                    <p className="text-gray-600 font-medium">
                        {dragOver ? 'Drop image here' : 'Click or drag image here'}
                    </p>
                    <p className="text-gray-400 text-sm mt-1">PNG, JPG up to 5MB</p>
                </div>
            )}
        </div>
    );
}
